/*

# `Angle` At A Glance

Angle is a fully-featured class, representing an angle in radians. It:
- has `radians` and `degrees` properties.
- has a constructor that takes a number of radians.
- has `sin`, `cos`, and `tan` properties.
- has `fromRadians` and `fromDegrees` static methods.
- has `add`, `sub`, `mul`, `div`, `normalized`, etc. methods.
- has `equals` and `isCloseTo` methods.

*/

import {
  fixedFloat,
  fixedNumber,
  NumberSchema,
  type Interpable,
  type Lerpable
} from '@utils';
import { ANGLE_EPSILON, ANGLE_PRECISION } from './GeomConstants';

// #region Constants

export const TWO_PI = Math.PI * 2;

// #endregion

// #region Free Functions

/**
 * Converts an angle from degrees to radians.
 *
 * @param degrees - The angle in degrees to be converted.
 * @returns The angle in radians.
 */
export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Converts an angle from radians to degrees.
 *
 * @param radians - The angle in radians to be converted.
 * @returns The angle in degrees.
 */
export function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Converts a given angle in turns (t) to radians.
 *
 * @param t - The angle in turns, where 1 turn is equal to 360 degrees.
 * @returns The angle in radians.
 */
export function tToRadians(t: number): number {
  return t * TWO_PI;
}

/**
 * Converts an angle in radians to a fraction of a full turn (T).
 *
 * @param radians - The angle in radians to convert.
 * @returns The equivalent fraction of a full turn (T).
 */
export function radiansToT(radians: number): number {
  return radians / TWO_PI;
}

/**
 * Normalizes an angle in radians to the range [0, 2π).
 *
 * This function ensures that the given angle in radians is wrapped within the
 * range from 0 (inclusive) to 2π (exclusive). It handles both positive and
 * negative input values.
 *
 * @param radians - The angle in radians to be normalized.
 * @returns The normalized angle in radians within the range [0, 2π).
 */
export function normalizeRadians(radians: number): number {
  return ((radians % TWO_PI) + TWO_PI) % TWO_PI;
}

/**
 * Normalizes an angle in radians to the range [-π, π].
 *
 * This function takes an angle in radians and adjusts it to ensure that
 * the resulting angle lies within the range from -π to π. This is useful
 * for ensuring that angular differences are represented in a consistent
 * manner.
 *
 * @param delta - The angle in radians to be normalized.
 * @returns The normalized angle in radians, within the range [-π, π].
 */
export function normalizeDeltaRadians(delta: number): number {
  delta = delta % TWO_PI;
  if (delta > Math.PI) {
    delta -= TWO_PI;
  } else if (delta < -Math.PI) {
    delta += TWO_PI;
  }
  return delta;
}

// #endregion

// #region Schema

export const AngleSchema = NumberSchema.transform(n => radians(n));
export const AngleDegreesSchema = NumberSchema.transform(n => degrees(n));

// #endregion

export function radians(radians: number): Angle {
  return new Angle(radians);
}

export function degrees(degrees: number): Angle {
  return new Angle({ degrees });
}

export function turns(t: number): Angle {
  return new Angle({ t });
}

/**
 * Represents an angle in radians with various utility methods for conversion,
 * interpolation, and arithmetic operations.
 *
 * @implements {Lerpable}
 * @implements {Interpable}
 *
 * @example
 * ```typescript
 * const angle1 = degrees(90);
 * const angle2 = radians(Math.PI / 2);
 * console.log(angle1.equals(angle2)); // true
 * ```
 */
export class Angle implements Lerpable, Interpable {
  readonly #radians: number;

  // #region Constructors

  /**
   * Constructs an instance of the Angle class.
   *
   * @param obj - The input parameter which can be one of the following:
   * - A number representing radians.
   * - An object with a `radians` property.
   * - An object with a `degrees` property.
   * - An object with a `t` property (in turns).
   *
   * @throws {Error} Throws an error if the input parameter does not match any of the expected types.
   */
  constructor(obj: { radians: number } | { degrees: number } | { t: number } | number) {
    if (typeof obj === 'number') {
      this.#radians = obj;
    } else if ('radians' in obj) {
      this.#radians = obj.radians;
    } else if ('degrees' in obj) {
      this.#radians = degreesToRadians(obj.degrees);
    } else if ('t' in obj) {
      this.#radians = tToRadians(obj.t);
    } else {
      throw new Error('Invalid arguments');
    }
  }

  static ZERO = new Angle(0);

  /**
   * Creates an instance of `Angle` from the given radians.
   *
   * @param radians - The angle in radians.
   * @returns A new `Angle` instance.
   */
  static fromRadians(radians: number): Angle {
    return new Angle({ radians });
  }

  /**
   * Creates an instance of `Angle` from the given degrees.
   *
   * @param degrees - The angle in degrees.
   * @returns A new `Angle` instance.
   */
  static fromDegrees(degrees: number): Angle {
    return new Angle({ degrees });
  }

  /**
   * Creates an instance of `Angle` from a given parameter `t` in turns.
   *
   * @param t - The angle in turns, where 1 turn is equal to 360 degrees.
   * @returns A new `Angle` instance.
   */
  static fromT(t: number): Angle {
    return new Angle({ t });
  }

  // #endregion

  // #region Comparison

  /**
   * Compares the current angle with another angle or a number representing radians.
   *
   * @param other - The angle or number to compare with. If a number is provided, it is treated as radians.
   * @returns `true` if the angles are equal, `false` otherwise.
   */
  equals(other: Angle | number): boolean {
    const radians = typeof other === 'number' ? other : other.radians;
    return this.radians === radians;
  }

  /**
   * Determines if the current angle is close to another angle within a specified tolerance.
   *
   * @param other - The angle to compare against, which can be an instance of `Angle` or a number representing radians.
   * @param tolerance - The tolerance within which the angles are considered close. Defaults to `ANGLE_EPSILON`.
   * @returns `true` if the angles are within the specified tolerance, `false` otherwise.
   */
  isCloseTo(other: Angle | number, tolerance: number = ANGLE_EPSILON): boolean {
    const radians = typeof other === 'number' ? other : other.radians;
    const diff = Math.abs(this.radians - radians) % TWO_PI;
    return diff < tolerance || Math.abs(diff - TWO_PI) < tolerance;
  }

  // #endregion

  // #region Properties

  /**
   * Gets the angle in radians.
   *
   * @returns The angle in radians.
   */
  get radians(): number {
    return this.#radians;
  }

  /**
   * Gets the angle in degrees.
   *
   * @returns {number} The angle in degrees.
   */
  get degrees(): number {
    return radiansToDegrees(this.radians);
  }

  /**
   * Gets the angle in turns, where 1 turn is equal to 360 degrees.
   *
   * @returns {number} The angle in turns.
   */
  get t(): number {
    return radiansToT(this.radians);
  }

  /**
   * Gets the sine of the angle.
   *
   * @returns The sine of the angle in radians.
   */
  get sin(): number {
    return Math.sin(this.radians);
  }

  /**
   * Gets the cosine of the angle.
   *
   * @returns {number} The cosine of the angle in radians.
   */
  get cos(): number {
    return Math.cos(this.radians);
  }

  /**
   * Gets the tangent of the angle.
   *
   * @returns The tangent of the angle in radians.
   */
  get tan(): number {
    return Math.tan(this.radians);
  }

  // #endregion

  // #region Interpolation

  /**
   * Linearly interpolates between this angle and the given end angle. The
   * interpolation is performed in the shortest direction.
   *
   * @param t - The interpolation factor, typically between 0 and 1.
   * @param end - The target angle to interpolate towards.
   * @returns A new `Angle` instance representing the interpolated angle.
   */
  lerp(t: number, end: this): this {
    // Always interpolate in the shortest direction
    const _start = this.normalized.radians;
    const _end = end.normalized.radians;
    const diff = _end - _start;
    const shortest = ((diff + Math.PI) % TWO_PI) - Math.PI;
    return radians(_start + t * shortest) as this;
  }

  /**
   * Returns a function that interpolates between the current angle and the
   * given end angle. The interpolation is performed in the shortest direction.
   *
   * @param end - The target angle to interpolate towards.
   * @returns A function that takes a parameter `t` (ranging from 0 to 1) and
   * returns the interpolated angle.
   */
  interp(end: this): (t: number) => this {
    // Always interpolate in the shortest direction
    const _start = this.normalized.radians;
    const _end = end.normalized.radians;
    const diff = _end - _start;
    const shortest = ((diff + Math.PI) % TWO_PI) - Math.PI;
    return (t: number) => radians(_start + t * shortest) as this;
  }

  // #endregion

  // #region Arithmetic

  /**
   * Adds the given angle or number of radians to the current angle.
   *
   * @param other - The angle or number in radians to add.
   * @returns A new `Angle` instance representing the sum of the current angle
   * and the other angle.
   */
  add(other: Angle | number): Angle {
    const phi = typeof other === 'number' ? other : other.radians;
    return radians(this.radians + phi);
  }

  /**
   * Subtracts the given angle or number of radians from the current angle.
   *
   * @param other - The angle or number of radians to subtract. If an `Angle`
   * instance is provided, its radians value will be used.
   * @returns A new `Angle` instance representing the result of the subtraction.
   */
  sub(other: Angle | number): Angle {
    const phi = typeof other === 'number' ? other : other.radians;
    return radians(this.radians - phi);
  }

  /**
   * Multiplies the current angle by a given factor.
   *
   * @param factor - The factor by which to multiply the angle.
   * @returns A new `Angle` instance with the multiplied value.
   */
  mul(factor: number): Angle {
    return radians(this.radians * factor);
  }

  /**
   * Divides the current angle by the given divisor and returns a new Angle
   * instance.
   *
   * @param divisor - The number by which to divide the current angle.
   * @returns A new Angle instance representing the result of the division.
   */
  div(divisor: number): Angle {
    return radians(this.radians / divisor);
  }

  /**
   * Returns a new `Angle` instance with the negated value of the current
   * angle's radians.
   *
   * @returns {Angle} A new `Angle` instance pointing in the opposite direction.
   */
  get negated(): Angle {
    return radians(-this.radians);
  }

  /**
   * Gets a new `Angle` instance with the radians value normalized to the range
   * [0, 2π).
   *
   * @returns {Angle} A new `Angle` instance with the normalized radians.
   */
  get normalized(): Angle {
    return radians(normalizeRadians(this.radians));
  }

  // #endregion

  // #region Serialization

  /**
   * Converts the angle to its JSON representation.
   *
   * @returns {any} The angle in radians.
   */
  toJSON(): unknown {
    return fixedFloat(this.radians, ANGLE_PRECISION);
  }

  /**
   * Creates an instance of `Angle` from a JSON object.
   *
   * @param json - The JSON object to convert. Expected to be a number.
   * @returns An instance of `Angle`.
   * @throws Will throw an error if the JSON object is not a number.
   */
  static fromJSON(json: unknown): Angle {
    return AngleSchema.parse(json);
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `Angle` as a string for debugging as degrees in `θ°` format.
   *
   * @param fractionDigits - The number of digits after the decimal point.
   * Default is determined by `fixedNumber`.
   * @returns The formatted string.
   */
  toStringDegrees(fractionDigits: number = ANGLE_PRECISION): string {
    return fixedNumber(this.degrees, fractionDigits) + '°';
  }

  /**
   * Formats the `Angle` as a string for debugging as radians in `θ rad` format.
   *
   * @param fractionDigits - The number of digits after the decimal point.
   * Default is determined by `Number.prototype.toFixed`.
   * @returns The formatted string.
   */
  toStringRadians(fractionDigits: number = ANGLE_PRECISION): string {
    return fixedNumber(this.radians, fractionDigits) + ' rad';
  }
}
